import { Fragment, useContext, useEffect } from 'react'
import { MenuTitles, Pages } from 'constants/menu'
import { AgencyContext } from 'contexts'
import { ContentLayout, PageLayout } from 'layout'
import { StaffAction } from 'types/staff'
import { useLocation, useNavigate } from 'react-router-dom'
import { PlaceHolderImage } from 'components'
import {
  AgxBodyText,
  AgxHeader,
  AgxColumn,
  AgxPhoneOutline,
} from '@urbanx/agx-ui-components'
import {
  AgxBadge,
  AgxButton,
  AgxTypography,
  Icon_Email,
} from '@urbanx/agx.ui-components2'
import { StaffModel, useGetAllStaff } from '@urbanx/agx.staff.client'
import { AuthContext } from '@urbanx/agx.auth.client'

const StaffMembers = () => {
  const { userAccount } = useContext(AuthContext)
  const [selectedAgency] = useContext(AgencyContext)
  const navigate = useNavigate()
  const location = useLocation()

  const isStaff = true
  const { data: staffMembers, isPending: allStaffIsPending } = useGetAllStaff()

  const renderStaffMember = (staffMember: StaffModel) => {
    return (
      <Fragment key={staffMember.email}>
        <tr className="tableRowStyle dataRow">
          <td width={'76px'}>
            <div className="thStatusContainer__teams">
              {staffMember.photo ? (
                <img
                  src={staffMember.photo.publicPath}
                  style={{ height: 60, width: 60, borderRadius: '50%' }}
                />
              ) : (
                <PlaceHolderImage size={60} title={staffMember.firstName} />
              )}
            </div>
          </td>
          <td>
            <div>
              <AgxTypography
                id={`txtStaffName-${staffMember.id}`}
                type="body"
                size="medium"
              >{`${staffMember.firstName} ${staffMember.lastName}`}</AgxTypography>
            </div>
            <div>
              <AgxTypography
                id={`txtStaffRole-${staffMember.id}`}
                type="caption"
                size="medium"
                color="var(--text-tertiary)"
              >
                {staffMember.title}
              </AgxTypography>
            </div>
          </td>
          <td className="memberNameStyle">
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon_Email color="var(--fg-tertiary)" width={14} height={14} />
                <AgxBodyText small extraClasses={'greyColor__teams'}>
                  {staffMember.email}
                </AgxBodyText>
              </div>
            </div>
            <div>
              {staffMember.phoneNumber && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <AgxPhoneOutline
                    width={14}
                    height={14}
                    color="var(--fg-tertiary)"
                  />
                  <AgxBodyText small extraClasses={'greyColor__teams'}>
                    {staffMember.phoneNumber}
                  </AgxBodyText>
                </div>
              )}
            </div>
          </td>
          <td>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              {staffMember.roles?.map((role) => (
                <AgxBadge
                  key={role}
                  size="sm"
                  type="colourfill"
                  colour="grey"
                  text={role}
                />
              ))}
            </div>
          </td>
          <td align="right">
            <div className="actionContainerTD">
              <AgxButton
                id={`btn-edit-staff-${staffMember.id ?? staffMember.email ?? 'unknown'}`}
                text="Edit"
                size="medium"
                variant="hollow"
                disabled={!staffMember.id}
                onClick={() => {
                  if (staffMember.id) {
                    navigate(`${Pages.EDIT_STAFF}/${staffMember.id}`)
                  }
                }}
              />
            </div>
          </td>
        </tr>
      </Fragment>
    )
  }

  useEffect(() => {
    if (location.state?.action === StaffAction.SELF_EDIT && staffMembers) {
      const currentUser = staffMembers.find(
        (staffMember) =>
          staffMember.id === userAccount?.agent ||
          staffMember.email === userAccount?.agent
      )

      if (currentUser?.id) {
        navigate(`${Pages.EDIT_STAFF}/${currentUser.id}`, {
          replace: true,
          state: { action: StaffAction.SELF_EDIT },
        })
      }
    }
  }, [location.state, navigate, staffMembers, userAccount?.agent])

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.TEAM}
      isStaff={isStaff}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.TEAM}
        isStaff={isStaff}
      >
        <AgxColumn extraLargeGap>
          <table width={'100%'} style={{ paddingBottom: 30 }}>
            <tbody>
              <tr className="tableRowStyle headerRow">
                <th colSpan={2} align="left">
                  <AgxHeader size={5}>Staff Members</AgxHeader>
                </th>
                <th align="left">
                  <AgxBodyText small>Details</AgxBodyText>
                </th>
                <th align="left">
                  <AgxBodyText small>Role(s)</AgxBodyText>
                </th>
                <th align="left"></th>
              </tr>
              {staffMembers &&
                staffMembers.map((staffMember) =>
                  renderStaffMember(staffMember)
                )}
              {!allStaffIsPending && (
                <tr>
                  <td colSpan={6} className="tdAddUser__teams">
                    <AgxButton
                      id="btn-add-member"
                      text="Add member"
                      size="medium"
                      variant="hollow"
                      onClick={() => navigate(Pages.CREATE_STAFF)}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  )
}

export default StaffMembers
